import { useOnboardingState } from '@/api/hooks/use-onboarding';
import { SETUP_MILESTONES, getFirstIncompleteConfigureHref } from '@/config/setup-milestones';
import type { SetupMilestone } from '@/shared/components/SetupProgress';

/** Setup progress from API — milestones link to Configure sections. */
export function useSetupProgress(): {
  milestones: SetupMilestone[];
  percent: number;
  isComplete: boolean;
  isLoading: boolean;
  resumeHref: string;
} {
  const { data, isLoading } = useOnboardingState();

  if (data?.steps?.length) {
    const milestones: SetupMilestone[] = SETUP_MILESTONES.map((def) => {
      const step = data.steps.find((s) => s.id === def.id);
      const fromApi = data.milestones?.find((m) => m.id === def.id);
      const complete = fromApi?.complete ?? step?.status === 'completed';
      return {
        id: def.id,
        label: def.label,
        complete,
        current: step?.status === 'current',
        blocked: step?.status === 'failed',
        configureHref:
          step?.configure_href ?? fromApi?.configure_href ?? def.configureHref ?? null,
      };
    });

    return {
      milestones,
      percent: data.percent_complete,
      isComplete: data.is_complete,
      isLoading,
      resumeHref: data.current_configure_href ?? getFirstIncompleteConfigureHref(milestones),
    };
  }

  const milestones: SetupMilestone[] = SETUP_MILESTONES.map((def, index) => ({
    id: def.id,
    label: def.label,
    complete: index === 0,
    current: index === 1,
    configureHref: def.configureHref ?? null,
  }));

  const completed = milestones.filter((m) => m.complete).length;

  return {
    milestones,
    percent: Math.round((completed / milestones.length) * 100),
    isComplete: false,
    isLoading,
    resumeHref: getFirstIncompleteConfigureHref(milestones),
  };
}
